import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InputTextarea, MinimizeIcon, SKIP_TARGET_ATTR, UndoIcon } from '@repo/atomic-components';
import { HeaderIconButton } from '@/ui/pages/ChatPage/components/HeaderIconButton';
import { CaseMentionChip } from './CaseMentionChip';
import { ImprovePromptIcon } from './ImprovePromptIcon';
import type { CaseMentionState } from '../hooks/useCaseMention';
import { DocumentsList } from './DocumentsList';
import { ChatInputFileControls } from './ChatInputFileControls';
import { DictationControl } from './DictationControl';
import { PromptPlaceholderOverlay } from './PromptPlaceholderOverlay';
import { spliceDictatedText } from '../utils/spliceDictatedText';
import { findPromptPlaceholders } from '../utils/promptPlaceholders';
import { ImprovePromptQuestions, SelectedAnswer } from './ImprovePromptQuestions';
import { useDictation } from '../hooks/useDictation';
import { useChatInputMaxHeight } from '../hooks/useChatInputMaxHeight';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';
import { ChatDocument } from '@/store';
import { ToolkitModal } from '@/ui/components/ToolkitModal';
import { useChatContext, useChatQuery } from '../useChatContext';
import { classNames as cn } from 'primereact/utils';
import { useResizeObserver } from '@/hooks';
import { FileStatus } from '@/types/file';
import { CHAT_INPUT_TEXTAREA_ID } from './NextSteps';
import { KEEPS_CHAT_INPUT_OPEN_ATTR } from '../constants';
import { extractFileListFromDataTransfer } from '@/utils/extractFileListFromDataTransfer';
import { ClarifierQuestion } from '@/api/chat';
import type { ToolkitTool } from '@/api/toolkit';
import type { MessageAttachedDocument } from '@/types/chat';
import { AddMatterFilesModal } from '../matterChat';
import { UploadFileButton } from './UploadFileButton';
import { PrimaryActionButton } from './PrimaryActionButton';
import { StopResponseButton } from './StopResponseButton';

const MAX_QUERY_LENGTH = 50000;
const QUERY_COUNTER_THRESHOLD = 200;

/** Marks the collapsed box's own buttons so the container's expand-on-mousedown lets their clicks through. */
const STRIP_CONTROL_ATTR = 'data-chat-input-strip-control';

export interface ChatInputProps {
  chatQuery: string;
  onInputChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSendMessage: () => void;
  isLoading: boolean;
  isImprovingPrompt: boolean;
  isRevertVisible: boolean;
  onImprovePrompt: () => void;
  onRevertPrompt: () => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onUploadFilesClick: () => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  sessionDocuments: ChatDocument[];
  onDeleteFile: (documentId: string) => void;
  hasFileErrors: boolean;
  /**
   * Optional: controls for case page usage and file-count warnings.
   */
  showFileControls?: boolean;
  filesToQueryCount?: number;
  fileCountWarningThreshold?: number;
  fileCountWarningMessage?: string;

  /** Shows an X next to the file-count chip to clear the selection. */
  onClearFilesSelection?: () => void;

  isModal?: boolean;

  onStopGeneratingResponse?: () => void;

  onStopImprovingPrompt?: () => void;

  /**
   * Required for voice input: updates the chat query from speech-to-text.
   */
  setChatQuery: (query: string) => void;

  placeholder?: string;

  minimized?: boolean;
  onExpand?: () => void;

  /**
   * When provided, clicking outside the expanded box shrinks it back to the small mode.
   */
  onCollapse?: () => void;

  /**
   * When provided, the collapsed strip carries a minimise control that dismisses the box
   * entirely (down to `MinimizedChatInputButton`). Omit to hide the control.
   */
  onMinimize?: () => void;

  /** Disables textarea auto-resize (e.g. on the case page). */
  disableAutoResize?: boolean;

  /**
   * Improve prompt clarifier questions to display above the input.
   */
  clarifierQuestions?: ClarifierQuestion[] | null;
  isFetchingClarifiers?: boolean;
  onClarifierAnswersComplete?: (answers: SelectedAnswer[]) => void;

  /**
   * Case page: blocks Send while "select all files" is still loading pages.
   */
  isCaseFilesBulkSelecting?: boolean;

  /** Disables the entire input — no typing, no buttons. Used when files are still processing. */
  disabled?: boolean;

  /** Matter chat: opens the add-files modal instead of uploading immediately. */
  onAddMatterFilesClick?: () => void;
  isAddMatterFilesModalVisible?: boolean;
  onHideAddMatterFilesModal?: () => void;
  onSelectMatterFilesFromCase?: () => void;
  onUploadMatterFilesFromComputer?: () => void;
  pendingMatterDocuments?: MessageAttachedDocument[];
  onRemovePendingMatterDocument?: (documentId: string) => void;
  onToolkitPromptSelect?: (prompt: string) => void;

  caseRefForBack?: string;

  /** Makes the textarea the skip-link target. Only the standalone Chat page sets this. */
  isSkipLinkTarget?: boolean;

  /** Frosted translucent background instead of solid white. Used on the new-chat page. */
  frostedBackground?: boolean;

  /** Case mention support: the scales button, `#` search, and the case chip. Only used on the new-chat page. */
  caseMention?: CaseMentionState;
}

export const ChatInputBase = ({
  chatQuery,
  onInputChange,
  onKeyDown,
  onSendMessage,
  isLoading,
  isImprovingPrompt,
  isRevertVisible,
  onImprovePrompt,
  onRevertPrompt,
  fileInputRef,
  onUploadFilesClick,
  onFileChange,
  sessionDocuments,
  onDeleteFile,
  hasFileErrors,
  showFileControls = true,
  filesToQueryCount,
  fileCountWarningThreshold,
  fileCountWarningMessage,
  onClearFilesSelection,
  isModal = true,
  onStopGeneratingResponse,
  placeholder,
  minimized = false,
  onExpand,
  onCollapse,
  onMinimize,
  disableAutoResize,
  clarifierQuestions,
  isFetchingClarifiers,
  onClarifierAnswersComplete,
  isCaseFilesBulkSelecting = false,
  disabled = false,
  onAddMatterFilesClick,
  isAddMatterFilesModalVisible = false,
  onHideAddMatterFilesModal,
  onSelectMatterFilesFromCase,
  onUploadMatterFilesFromComputer,
  pendingMatterDocuments = [],
  caseRefForBack,
  onRemovePendingMatterDocument,
  onToolkitPromptSelect,
  isSkipLinkTarget = false,
  frostedBackground = false,
  caseMention,
}: ChatInputProps) => {
  const frostedBgClass = useFrostedGlassClass();
  const [isToolkitModalVisible, setIsToolkitModalVisible] = useState(false);
  const [isWarningTooltipVisible, setIsWarningTooltipVisible] = useState(false);
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  // Show the bottom fade only while more files are below.
  const [filesRowEl, setFilesRowEl] = useState<HTMLDivElement | null>(null);
  const [filesRowCanScrollUp, setFilesRowCanScrollUp] = useState(false);
  const [filesRowCanScrollDown, setFilesRowCanScrollDown] = useState(false);
  // Show the text fades only when there's more to scroll that way.
  const [textareaCanScrollUp, setTextareaCanScrollUp] = useState(false);
  const [textareaCanScrollDown, setTextareaCanScrollDown] = useState(false);

  // Set on the next tick so the "Assessing…" label fades in.
  const [assessLabelVisible, setAssessLabelVisible] = useState(false);
  // Cycles 1 → 2 → 3 → 1 for the trailing dots after "Assessing".
  const [assessDotCount, setAssessDotCount] = useState(1);
  const isImproveActive = isImprovingPrompt || isFetchingClarifiers;
  // Hidden while the clarifier widget is open; it shows the same icon.
  const isImprovePromptShown =
    chatQuery.trim().length > 10 && !(clarifierQuestions != null && clarifierQuestions.length > 0);
  useEffect(() => {
    if (!isImproveActive) {
      setAssessLabelVisible(false);
      return;
    }
    const raf = window.requestAnimationFrame(() => setAssessLabelVisible(true));
    return () => window.cancelAnimationFrame(raf);
  }, [isImproveActive]);
  useEffect(() => {
    if (!isImproveActive) {
      setAssessDotCount(1);
      return;
    }
    const interval = window.setInterval(() => {
      setAssessDotCount((prev) => (prev % 3) + 1);
    }, 400);
    return () => window.clearInterval(interval);
  }, [isImproveActive]);

  // Refs for moving focus when entering/exiting dictation.
  const startDictationBtnRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const maxTextareaHeight = useChatInputMaxHeight({ containerElement, textareaRef });

  const measureFilesRowScroll = useCallback(() => {
    if (!filesRowEl) {
      setFilesRowCanScrollUp(false);
      setFilesRowCanScrollDown(false);
      return;
    }
    const remainingBelow = filesRowEl.scrollHeight - filesRowEl.scrollTop - filesRowEl.clientHeight;
    setFilesRowCanScrollUp(filesRowEl.scrollTop > 0);
    setFilesRowCanScrollDown(remainingBelow > 1);
  }, [filesRowEl]);
  useResizeObserver(filesRowEl, undefined, measureFilesRowScroll);

  const measureTextareaScroll = useCallback(() => {
    const el = textareaRef.current;
    if (!el || minimized) {
      setTextareaCanScrollUp(false);
      setTextareaCanScrollDown(false);
      return;
    }
    const remainingBelow = el.scrollHeight - el.scrollTop - el.clientHeight;
    setTextareaCanScrollUp(el.scrollTop > 0);
    setTextareaCanScrollDown(remainingBelow > 1);
  }, [minimized]);

  // Collapse on a click outside: decided on `mousedown`, before popovers unmount, but done once the click
  // has landed, as collapsing moves what's under the pointer. Not attached while a chat-input modal is
  // open — those are portalled, so their clicks look "outside".
  const isChatInputModalOpen =
    isToolkitModalVisible ||
    isAddMatterFilesModalVisible ||
    (clarifierQuestions != null && clarifierQuestions.length > 0);
  useEffect(() => {
    if (minimized || !onCollapse || !containerElement || isChatInputModalOpen) return;
    let isCollapsePending = false;
    let collapseTimeout: ReturnType<typeof setTimeout> | undefined;
    const handlePointerDown = (event: MouseEvent) => {
      // Checks the event's path, not its target: a click can remove its own target, like a
      // placeholder pill that's filled in on mousedown.
      const path = event.composedPath();
      isCollapsePending =
        !path.includes(containerElement) &&
        !path.some((node) => node instanceof Element && node.hasAttribute(KEEPS_CHAT_INPUT_OPEN_ATTR));
    };
    const handlePointerUp = () => {
      if (!isCollapsePending) return;
      isCollapsePending = false;
      // A task later, so after the click that follows `mouseup`.
      collapseTimeout = setTimeout(onCollapse);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('mouseup', handlePointerUp);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('mouseup', handlePointerUp);
      clearTimeout(collapseTimeout);
    };
  }, [minimized, onCollapse, containerElement, isChatInputModalOpen]);

  /**
   * Resize based on the query only. PrimeReact's `autoResize` also measures on mount, when the
   * panel can be ~14px wide, and gets stuck at max height.
   */
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Nothing typed means the base height from CSS, and it keeps the placeholder - the only text
    // present at that point - out of the measurement below.
    if (disableAutoResize || minimized || !chatQuery) {
      textarea.style.height = '';
      measureTextareaScroll();
      return;
    }

    // Collapse first so shrinking the text shrinks the box; max-height keeps the growth bounded.
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
    measureTextareaScroll();
  }, [chatQuery, disableAutoResize, minimized, maxTextareaHeight, measureTextareaScroll]);

  // A textarea that mounts with text (a restored unsent prompt) has its caret at the start.
  // Move it to the end, so dictation and typing continue the prompt. Not a layout effect:
  // PrimeReact attaches the textarea ref in an effect.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, []);

  // Live dictation: `onStart` saves the text and caret, and the transcript is inserted at that
  // caret as it grows. Cancel restores the saved text; Done keeps it.
  const dictationSnapshotRef = useRef<{ base: string; caret: number } | null>(null);
  // The caret is tracked here rather than read back from the textarea. After a click on the
  // dictation controls, Chrome moves an unfocused textarea's caret to the start when `readOnly`
  // toggles or Cancel shortens the text.
  const dictationCaretRef = useRef<number | null>(null);
  // Where the last dictation left the caret, kept until the text changes or the textarea is focused.
  const caretAfterDictationRef = useRef<{ caret: number; text: string } | null>(null);

  const dictation = useDictation({
    onStart: () => {
      const caret = Math.min(dictationCaretRef.current ?? chatQuery.length, chatQuery.length);
      dictationSnapshotRef.current = { base: chatQuery, caret };
      dictationCaretRef.current = caret;
    },
    onCancel: () => {
      const snapshot = dictationSnapshotRef.current;
      if (!snapshot) return;
      onInputChange({ target: { value: snapshot.base } } as ChangeEvent<HTMLTextAreaElement>);
      dictationCaretRef.current = snapshot.caret;
      dictationSnapshotRef.current = null;
    },
    onCommit: () => {
      // Field already reflects the live splice — clear the snapshot so a
      // next start begins fresh.
      dictationSnapshotRef.current = null;
    },
  });
  // Set on mic click so the row rearranges immediately, before the recogniser is actually listening.
  const [isDictationStarting, setIsDictationStarting] = useState(false);
  useEffect(() => {
    if (dictation.listening) setIsDictationStarting(false);
  }, [dictation.listening]);

  const handleStartDictation = () => {
    // Read before the textarea turns read-only.
    const saved = caretAfterDictationRef.current;
    dictationCaretRef.current = saved?.text === chatQuery ? saved.caret : (textareaRef.current?.selectionStart ?? null);
    setIsDictationStarting(true);
    dictation.startListening();
  };

  const isDictationActive = dictation.listening || dictation.isFinalizing || isDictationStarting;

  // Keeps going while finalizing, so the last phrase flushed after Done still lands.
  const isDictationTranscribing = dictation.listening || dictation.isFinalizing;
  useEffect(() => {
    if (!isDictationTranscribing) return;
    const snapshot = dictationSnapshotRef.current;
    if (!snapshot) return;
    const newText = spliceDictatedText(snapshot.base, snapshot.caret, dictation.transcript);
    dictationCaretRef.current = newText.length - (snapshot.base.length - snapshot.caret);
    if (newText !== chatQuery) {
      onInputChange({ target: { value: newText } } as ChangeEvent<HTMLTextAreaElement>);
    }
    // Don't depend on `chatQuery` — the splice changes it and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.transcript, isDictationTranscribing]);

  // Once the textarea is editable again, put the caret after the dictated text, or back where it
  // was on Cancel, so the next dictation carries on from there.
  const wasDictationTranscribingRef = useRef(false);
  useEffect(() => {
    const wasTranscribing = wasDictationTranscribingRef.current;
    wasDictationTranscribingRef.current = isDictationTranscribing;
    if (!wasTranscribing || isDictationTranscribing) return;

    const textarea = textareaRef.current;
    const caret = dictationCaretRef.current;
    dictationCaretRef.current = null;
    if (!textarea || caret == null) return;
    const position = Math.min(caret, textarea.value.length);
    textarea.setSelectionRange(position, position);
    caretAfterDictationRef.current = { caret: position, text: textarea.value };
  }, [isDictationTranscribing]);

  // Focus goes back to the mic on cancel and to the textarea on commit.
  const dictationExitFocusTargetRef = useRef<'input' | 'mic' | null>(null);
  const wasDictationActiveRef = useRef(false);

  useEffect(() => {
    const wasActive = wasDictationActiveRef.current;
    wasDictationActiveRef.current = isDictationActive;

    if (isDictationActive === wasActive) return;
    if (isDictationActive) return;

    const exitTarget = dictationExitFocusTargetRef.current;
    dictationExitFocusTargetRef.current = null;

    if (exitTarget === 'input') {
      textareaRef.current?.focus();
    } else if (exitTarget === 'mic') {
      startDictationBtnRef.current?.focus();
    }
  }, [isDictationActive]);

  const handleToolSelect = (tool: ToolkitTool) => {
    if (tool.prompt) {
      if (onToolkitPromptSelect) {
        onToolkitPromptSelect(tool.prompt);
        return;
      }
      onInputChange({ target: { value: tool.prompt } } as ChangeEvent<HTMLTextAreaElement>);
    }
  };

  const handleMouseEnterSend = () => {
    if (shouldShowFileWarning) {
      setIsWarningTooltipVisible(true);
    }
  };

  const handleMouseLeaveSend = () => setIsWarningTooltipVisible(false);

  const shouldShowFileWarning =
    filesToQueryCount != null && fileCountWarningThreshold != null
      ? filesToQueryCount > fileCountWarningThreshold
      : false;

  const sessionDocumentsForInput = useMemo(
    () => sessionDocuments.filter((d) => d.isPromptAttachment !== false),
    [sessionDocuments],
  );

  const handlePasteFilesFromClipboard = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      if (!showFileControls) return;

      const fileList = extractFileListFromDataTransfer(e.clipboardData);
      if (!fileList || fileList.length === 0) return;

      e.preventDefault();
      onFileChange({
        target: { files: fileList },
      } as ChangeEvent<HTMLInputElement>);
    },
    [showFileControls, onFileChange],
  );

  const onConfirmDictation = () => {
    // Dictated text lands in the textarea, so that is where the user continues.
    dictationExitFocusTargetRef.current = 'input';
    setIsDictationStarting(false);
    try {
      dictation.confirmListening();
    } catch (error) {
      console.error('Failed to set chat query:', error);
    }
  };

  const onCancelDictation = () => {
    dictationExitFocusTargetRef.current = 'mic';
    setIsDictationStarting(false);
    try {
      dictation.cancelListening();
    } catch (error) {
      console.error('Failed to cancel dictation:', error);
    }
  };

  const isAnyDocumentUploading = useMemo(
    () => sessionDocumentsForInput.some((d) => d.status === FileStatus.UPLOADING),
    [sessionDocumentsForInput],
  );

  useEffect(() => {
    return () => dictation.cancelListening();
    // cancelListening is stable (useCallback); full dictation object is a new reference each render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run cleanup on unmount / cancelListening identity change
  }, [dictation.cancelListening]);

  // The toolbar's Stop is hidden while collapsed, so the strip shows its own.
  const showStripStop = minimized && isLoading && onStopGeneratingResponse != null;

  const isTextareaDisabled =
    disabled ||
    isLoading ||
    isFetchingClarifiers ||
    isImprovingPrompt ||
    (clarifierQuestions != null && clarifierQuestions.length > 0);

  // Fade the top/bottom edge only when the text can scroll that way.
  const textareaFadeStyle = (() => {
    if (minimized || (!textareaCanScrollUp && !textareaCanScrollDown)) return {};
    const topStop = textareaCanScrollUp ? 'transparent 0, black 64px' : 'black 0';
    const bottomStop = textareaCanScrollDown ? 'black calc(100% - 64px), transparent 100%' : 'black 100%';
    const mask = `linear-gradient(to bottom, ${topStop}, ${bottomStop})`;
    return { maskImage: mask, WebkitMaskImage: mask };
  })();

  const promptPlaceholders = findPromptPlaceholders(chatQuery);
  const hasPromptPlaceholders = promptPlaceholders.length > 0;

  return (
    <div className="flex w-full flex-col items-center">
      <div
        className={cn(
          'grid w-full max-w-248.25 transition-[grid-template-rows] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          minimized ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
        )}
        inert={minimized}
      >
        <div className="min-h-0 overflow-hidden">
          {clarifierQuestions && clarifierQuestions.length > 0 && onClarifierAnswersComplete && (
            <div
              className={cn(
                'mb-3 w-full transition-[opacity,transform] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transform-none motion-reduce:transition-none',
                minimized ? '-translate-y-1 opacity-0' : 'translate-y-0 opacity-100',
              )}
            >
              <ImprovePromptQuestions questions={clarifierQuestions} onComplete={onClarifierAnswersComplete} />
            </div>
          )}
        </div>
      </div>
      <div
        ref={setContainerElement}
        className={cn(
          'border-theme-border-grey relative w-full max-w-248.25 rounded-3xl border shadow-sm transition-shadow hover:shadow-md',
          frostedBackground ? frostedBgClass : 'bg-theme-white',
          minimized ? 'cursor-pointer px-[18px] py-[13px]' : 'p-5 pt-6',
          disabled && 'pointer-events-none opacity-60',
        )}
        onMouseDownCapture={(event) => {
          if (!minimized) return;
          // The strip's buttons live inside the box, and this listener captures ahead of them.
          if ((event.target as HTMLElement).closest(`[${STRIP_CONTROL_ATTR}]`)) return;
          onExpand?.();
        }}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest('button, a, textarea, input, select, [role="button"]')) return;
          textareaRef.current?.focus();
        }}
      >
        {caseMention?.selectedCase && (
          <CaseMentionChip caseData={caseMention.selectedCase} onRemove={caseMention.clearCase} />
        )}
        {((typeof filesToQueryCount === 'number' && filesToQueryCount > 0) ||
          (showFileControls &&
            !isLoading &&
            (pendingMatterDocuments.length > 0 || sessionDocumentsForInput.length > 0))) && (
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
              minimized ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
            )}
            inert={minimized}
          >
            <div style={{ clipPath: 'inset(0 -100vw)' }} className="min-h-0 min-w-0">
              {typeof filesToQueryCount === 'number' && filesToQueryCount > 0 && (
                <div
                  className={cn(
                    'mb-4 transition-[opacity,transform] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transform-none motion-reduce:transition-none',
                    minimized ? '-translate-y-1 opacity-0' : 'translate-y-0 opacity-100',
                  )}
                >
                  {/* Uses the upload chip for the "X items selected" summary. No extension, so it gets the generic icon. */}
                  <UploadFileButton
                    documentId="selected-files-summary"
                    documentName={`${filesToQueryCount} ${filesToQueryCount === 1 ? 'item selected' : 'items selected'}`}
                    onRemove={onClearFilesSelection ? () => onClearFilesSelection() : undefined}
                  />
                </div>
              )}
              {showFileControls &&
                !isLoading &&
                (pendingMatterDocuments.length > 0 || sessionDocumentsForInput.length > 0) && (
                  <div className="relative -mx-5 mb-4">
                    <div
                      ref={setFilesRowEl}
                      onScroll={measureFilesRowScroll}
                      className={cn(
                        'hide-scrollbar flex max-h-[100px] flex-wrap gap-2 overflow-y-auto px-5 pb-1 transition-[opacity,transform] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transform-none motion-reduce:transition-none',
                        minimized ? '-translate-y-1 opacity-0' : 'translate-y-0 opacity-100',
                      )}
                      style={(() => {
                        // Mask-fade the edges that have more content to scroll to.
                        if (!filesRowCanScrollUp && !filesRowCanScrollDown) return undefined;
                        const topStop = filesRowCanScrollUp ? 'transparent 0, black 24px' : 'black 0';
                        const bottomStop = filesRowCanScrollDown
                          ? 'black calc(100% - 24px), transparent 100%'
                          : 'black 100%';
                        const mask = `linear-gradient(to bottom, ${topStop}, ${bottomStop})`;
                        return { maskImage: mask, WebkitMaskImage: mask };
                      })()}
                    >
                      {pendingMatterDocuments.map((doc) => (
                        <UploadFileButton
                          key={doc.documentId}
                          documentId={doc.documentId}
                          documentName={doc.documentName}
                          status={FileStatus.UPLOADED}
                          onRemove={onRemovePendingMatterDocument}
                        />
                      ))}
                      <DocumentsList documents={sessionDocumentsForInput} onDeleteFile={onDeleteFile} />
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
        <div className={cn('flex items-center', !minimized && '-mr-5')}>
          <div className="relative flex w-full min-w-0">
            <InputTextarea
              ref={textareaRef}
              {...(isSkipLinkTarget ? { [SKIP_TARGET_ATTR]: true } : {})}
              spellCheck
              autoCorrect="on"
              autoCapitalize="sentences"
              disabled={isTextareaDisabled}
              // Read-only while dictating, since typing would be overwritten by the next transcript update.
              readOnly={isDictationTranscribing}
              value={chatQuery}
              onChange={onInputChange}
              onKeyDown={onKeyDown}
              onPaste={handlePasteFilesFromClipboard}
              id={CHAT_INPUT_TEXTAREA_ID}
              aria-label="Chat message"
              placeholder={placeholder ?? 'What can I help you with today?'}
              className={cn(
                'hide-scrollbar !text-theme-black !placeholder-theme-text-grey w-full resize-none border-none !p-0 !pr-5 transition-[max-height] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] focus:ring-0 motion-reduce:transition-none',
                // With placeholders, the overlay draws the text and the textarea keeps only the caret.
                // Not `color`: the theme transitions it, which would fade the text in and out.
                hasPromptPlaceholders && '[-webkit-text-fill-color:transparent]',
                frostedBackground ? '!bg-transparent' : '!bg-theme-white',
                minimized ? 'max-h-11' : 'max-h-[96px] lg:max-h-[140px]',
              )}
              maxLength={MAX_QUERY_LENGTH}
              rows={1}
              style={
                minimized
                  ? { maxHeight: '2.75rem', overflowY: 'hidden' }
                  : { maxHeight: `${maxTextareaHeight}px`, overflowY: 'auto', ...textareaFadeStyle }
              }
              onScroll={measureTextareaScroll}
              onFocus={(e) => {
                // A mouse click then places the caret itself; this covers focus from the keyboard.
                const saved = caretAfterDictationRef.current;
                caretAfterDictationRef.current = null;
                if (saved?.text === e.currentTarget.value) {
                  e.currentTarget.setSelectionRange(saved.caret, saved.caret);
                }
                if (!minimized) return;
                onExpand?.();
              }}
            />
            {hasPromptPlaceholders && (
              <PromptPlaceholderOverlay
                value={chatQuery}
                placeholders={promptPlaceholders}
                disabled={Boolean(isTextareaDisabled)}
                interactive={!isTextareaDisabled && !isDictationTranscribing}
                style={textareaFadeStyle}
              />
            )}
          </div>
          {minimized && (onMinimize || showStripStop) && (
            <span className="flex shrink-0 items-center gap-2" {...{ [STRIP_CONTROL_ATTR]: '' }}>
              {onMinimize && (
                <HeaderIconButton
                  icon={<MinimizeIcon width={14} height={14} aria-hidden />}
                  tooltip="Hide the chat box"
                  tooltipPosition="top"
                  onClick={onMinimize}
                  className="!h-6 !w-6"
                />
              )}
              {showStripStop && <StopResponseButton onStop={onStopGeneratingResponse} size={24} />}
            </span>
          )}
        </div>
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
            minimized ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
          inert={minimized}
        >
          <div className="min-h-0">
            <div
              className={cn(
                'mt-5 flex flex-col items-center justify-between gap-6 transition-[opacity,transform] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transform-none motion-reduce:transition-none',
                minimized ? '-translate-y-1 opacity-0' : 'translate-y-0 opacity-100',
              )}
            >
              {!isDictationActive &&
                MAX_QUERY_LENGTH - chatQuery.length <= QUERY_COUNTER_THRESHOLD &&
                MAX_QUERY_LENGTH - chatQuery.length >= 0 && (
                  <span className="text-fletchers-green-800 mr-3 w-full text-right text-sm font-medium">
                    {chatQuery.length.toLocaleString()}/{MAX_QUERY_LENGTH.toLocaleString()} character limit
                  </span>
                )}
              <div className="@container flex w-full flex-nowrap gap-2">
                <div className={cn('w-full flex-nowrap gap-1', isDictationActive ? 'hidden @[25rem]:flex' : 'flex')}>
                  <>
                    {showFileControls && (
                      <>
                        <ChatInputFileControls
                          isLoading={isLoading}
                          isDisabled={isImprovingPrompt || isLoading}
                          fileInputRef={fileInputRef}
                          onFileChange={onFileChange}
                          onUploadFilesClick={onUploadFilesClick}
                          onAddMatterFilesClick={onAddMatterFilesClick}
                          onOpenToolkit={() => setIsToolkitModalVisible(true)}
                          // slotAfterUpload={
                          //   caseMention && (
                          //     <>
                          //       <HeaderIconButton
                          //         ref={caseMentionBtnRef}
                          //         icon={<ScalesIcon width={18} height={18} />}
                          //         tooltip="Link a case"
                          //         onClick={caseMention.openPopover}
                          //         // One case per new chat: disabled once a case is picked.
                          //         disabled={Boolean(caseMention.selectedCase)}
                          //         active={caseMention.isPopoverOpen}
                          //       />
                          //       <CaseMentionPopover
                          //         open={caseMention.isPopoverOpen}
                          //         onClose={caseMention.closePopover}
                          //         onSelect={caseMention.selectCase}
                          //         anchorRef={caseMentionBtnRef}
                          //       />
                          //     </>
                          //   )
                          // }
                        />
                      </>
                    )}
                    {isRevertVisible ? (
                      <HeaderIconButton
                        className="ml-0.5"
                        icon={<UndoIcon className="h-[16px] w-[16px]" />}
                        tooltip="Revert to original prompt"
                        onClick={onRevertPrompt}
                        disabled={isLoading}
                      />
                    ) : (
                      <div
                        className={cn(
                          'ml-0.5 overflow-visible transition-[width,opacity] duration-300 ease-out',
                          isImprovePromptShown ? 'w-8 opacity-100' : 'pointer-events-none w-0 opacity-0',
                        )}
                        // Keeps the hidden button out of the tab order and away from screen readers.
                        inert={!isImprovePromptShown}
                      >
                        <div className="relative">
                          <HeaderIconButton
                            icon={<ImprovePromptIcon isActive={isImproveActive} />}
                            tooltip={
                              isImprovingPrompt ? 'Improving' : isFetchingClarifiers ? 'Assessing' : 'Improve prompt'
                            }
                            onClick={onImprovePrompt}
                            disabled={isImproveActive}
                            className={isImproveActive ? '!opacity-100' : undefined}
                          />
                          {isImproveActive && (
                            <span
                              className={cn(
                                'text-theme-black pointer-events-none absolute top-1/2 left-full ml-1.5 flex -translate-y-1/2 items-center text-[0.8125rem] font-medium whitespace-nowrap',
                                'opacity-0 transition-opacity duration-300 ease-out',
                                assessLabelVisible && 'opacity-100',
                              )}
                            >
                              {isImprovingPrompt ? 'Improving' : 'Assessing'}
                              <span aria-hidden className="inline-block w-3 text-left">
                                {'.'.repeat(assessDotCount)}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                </div>
                <div className="relative ml-auto flex items-center gap-2">
                  <DictationControl
                    isSupported={dictation.browserSupportsSpeechRecognition}
                    isListening={dictation.listening}
                    isBusy={dictation.isFinalizing}
                    isStarting={isDictationStarting}
                    disabled={isImprovingPrompt || isLoading}
                    onStart={handleStartDictation}
                    onCancel={onCancelDictation}
                    onConfirm={onConfirmDictation}
                    startButtonRef={startDictationBtnRef}
                  />
                  <PrimaryActionButton
                    isLoading={isLoading}
                    isImprovingPrompt={isImprovingPrompt || isDictationActive}
                    isFetchingClarifiers={Boolean(isFetchingClarifiers)}
                    hasClarifierQuestions={clarifierQuestions != null && clarifierQuestions.length > 0}
                    isAnyDocumentUploading={isAnyDocumentUploading}
                    isCaseFilesBulkSelecting={isCaseFilesBulkSelecting}
                    chatQuery={chatQuery}
                    hasFileErrors={hasFileErrors}
                    onSendMessage={onSendMessage}
                    onStopGeneratingResponse={onStopGeneratingResponse}
                    onMouseEnterSend={handleMouseEnterSend}
                    onMouseLeaveSend={handleMouseLeaveSend}
                  />
                  {shouldShowFileWarning && isWarningTooltipVisible && (
                    <div className="pointer-events-none absolute right-0 bottom-[-70px] w-72 bg-[#FDECEE] px-3 py-2 text-[0.6875rem] leading-4 text-[#D9534F] shadow-sm">
                      {fileCountWarningMessage ??
                        'Querying many files will take some time. Try selecting specific files to query for a faster response.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isToolkitModalVisible && (
        <ToolkitModal
          visible={isToolkitModalVisible}
          onHide={() => setIsToolkitModalVisible(false)}
          onToolSelect={handleToolSelect}
          isModal={isModal}
        />
      )}
      {onHideAddMatterFilesModal && onSelectMatterFilesFromCase && onUploadMatterFilesFromComputer && (
        <AddMatterFilesModal
          visible={isAddMatterFilesModalVisible}
          onHide={onHideAddMatterFilesModal}
          onSelectFromCase={onSelectMatterFilesFromCase}
          onUploadFromComputer={onUploadMatterFilesFromComputer}
          onFileDrop={onFileChange}
          caseRefForBack={caseRefForBack}
        />
      )}
    </div>
  );
};

export const ChatInput = (props: Partial<ChatInputProps>) => {
  // Only the input subscribes to the prompt; everything else uses the stable chat API.
  const { chatQuery, isRevertVisible } = useChatQuery();
  const {
    setChatQuery,
    handleInputChange,
    handleKeyDown,
    handleSendMessage,
    isLoading,
    isImprovingPrompt,
    handleImprovePromptClick,
    handleRevertPromptClick,
    fileInputRef,
    handleUploadFilesClick,
    handleFileChange,
    sessionDocuments,
    handleDeleteFile,
    hasFileErrors,
    handleStopGeneratingResponse,
    handleStopImprovingPrompt,
    isFetchingClarifiers,
    clarifierQuestions,
    handleClarifierAnswersComplete,
    pendingMatterDocuments,
    removePendingMatterDocument,
    handleToolkitPromptSelect,
  } = useChatContext();

  return (
    <ChatInputBase
      chatQuery={isLoading ? '' : chatQuery}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onSendMessage={handleSendMessage}
      isLoading={isLoading}
      isImprovingPrompt={isImprovingPrompt}
      isRevertVisible={Boolean(isRevertVisible)}
      onImprovePrompt={handleImprovePromptClick}
      onRevertPrompt={handleRevertPromptClick}
      fileInputRef={fileInputRef}
      onUploadFilesClick={handleUploadFilesClick}
      onFileChange={handleFileChange}
      sessionDocuments={sessionDocuments}
      onDeleteFile={handleDeleteFile}
      hasFileErrors={hasFileErrors}
      onStopGeneratingResponse={handleStopGeneratingResponse}
      onStopImprovingPrompt={handleStopImprovingPrompt}
      setChatQuery={setChatQuery}
      isFetchingClarifiers={isFetchingClarifiers}
      clarifierQuestions={clarifierQuestions}
      onClarifierAnswersComplete={handleClarifierAnswersComplete}
      pendingMatterDocuments={pendingMatterDocuments}
      onRemovePendingMatterDocument={removePendingMatterDocument}
      onToolkitPromptSelect={handleToolkitPromptSelect}
      {...props}
    />
  );
};
